'use client';

import React, { useContext, useRef } from 'react';
import clsx from 'clsx';
import { AnimatePresence, motion } from 'framer-motion';
import { IconCheckmark } from '@frameio/vapor-icons';
import type { ThemeColorsMeta } from '@frameio/vapor-theme';
import { theme } from '@frameio/vapor-theme';
import styles from './Toggle.module.css';
import type { ToggleContextType } from './ToggleContext';
import { ToggleContext } from './ToggleContext';

type ToggleCheckboxVariants = 'primary' | 'over-image' | 'muted';

type ToggleCheckboxColors = 'primary' | ThemeColorsMeta;

export type ToggleCheckboxProps = {
  indeterminate?: boolean;
  readOnly?: boolean;
  color?: ToggleCheckboxColors;
  /** Can be used to set hover state based on parent hover state */
  isHovering?: boolean;
} & Partial<ToggleContextType> &
  React.HTMLAttributes<HTMLDivElement> & {
    variant?: ToggleCheckboxVariants;
  };

/**
 * This component renders a div that looks like a checkbox element.
 *
 * @warning
 * You should typically use the `Checkbox` component instead, which
 * wraps this component with the proper event handling, label, and other
 * accessibility features. Or you should use it as a descendant of
 * `Toggle`.
 *
 * @example
 * ```jsx
 * <Toggle onChange={onChange} checked={checked}>
 *   <ToggleCheckbox color='primary' />
 *   <ToggleLabel color='primary'>Label</ToggleLabel>
 * </Toggle>
 * ```
 */

export const ToggleCheckbox = React.memo(
  React.forwardRef(
    (
      {
        indeterminate,
        color: colorProp = 'primary',
        className,
        style,
        variant = 'primary',
        isHovering = false,
        ...other
      }: ToggleCheckboxProps,
      forwardedRef: React.Ref<HTMLDivElement>
    ) => {
      const context = useContext(ToggleContext);
      // To enable ToggleCheckbox to potentially be used outside of the Toggle
      // context provider, we fallback to allowing props to manage things
      // like checked, focused, disabled, etc.
      const { tapped, checked, disabled, hovering, focused } = context ?? other;

      const nodeRef = useRef(null);

      const color =
        colorProp === 'primary'
          ? theme.colors.brand.primary
          : theme.colors.meta.opaque[colorProp];

      return (
        <div
          ref={forwardedRef}
          className={clsx(styles.checkbox, className)}
          data-checked={checked || undefined}
          data-disabled={disabled || undefined}
          data-focus-visible={focused || undefined}
          data-hovered={isHovering || hovering || undefined}
          data-indeterminate={indeterminate || undefined}
          data-pressed={tapped || undefined}
          data-testid="toggle-checkbox"
          data-variant={variant}
          style={
            {
              ...style,
              '--vapor-toggle-color': color,
            } as React.CSSProperties
          }
          {...other}
        >
          <AnimatePresence initial={false}>
            {checked || indeterminate ? (
              <motion.div
                ref={nodeRef}
                animate={{ opacity: 1, scale: 1 }}
                className={styles.checkboxMotionCheckmark}
                data-checked={checked || undefined}
                data-disabled={disabled || undefined}
                data-indeterminate={indeterminate || undefined}
                exit={{ opacity: 0, scale: 0 }}
                initial={{ opacity: 0, scale: 0 }}
                transition={{
                  duration: 0.1,
                  ease: theme.transitions.curves.primary,
                }}
              >
                {indeterminate ? (
                  <div className={styles.checkboxIndeterminate} />
                ) : (
                  <IconCheckmark size={16} />
                )}
              </motion.div>
            ) : null}
          </AnimatePresence>
        </div>
      );
    }
  )
);
